import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { SubscriptionErrorReasons } from '@app/shared/+state/billing';
import { NotificationType, User } from '@app/shared/interfaces';
import { BillingService } from '@app/shared/services/billing/billing.service';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';
import { IntercomService } from '@app/shared/services/intercom.service';
import { OptimizelyService } from '@app/shared/services/optimizely/optimizely.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'sbnb-subscribe',
  templateUrl: './subscribe.component.html',
  styleUrls: ['./subscribe.component.scss'],
})
export class SubscribeComponent implements OnInit, OnDestroy {
  stage: number;
  subscriptionData: any;
  subscribing: boolean;
  loading: boolean;
  user: User;

  stashedNewPaymentMethod: any;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private billingService: BillingService,
    private authService: AuthenticationService,
    private router: Router,
    private toast: ToastNotificationsService,
    private deviceDetectionService: DeviceDetectionService,
    private optimizelyService: OptimizelyService,
    private segmentIoService: SegmentIoService,
    private intercomService: IntercomService
  ) { }

  ngOnInit() {
    this.loading = true;
    this.stage = 1;

    // If Billing is disabled in Optimizely, redirect
    this.optimizelyService.billingDisabled$.pipe(takeUntil(this.destroyed$)).subscribe((disabled) => {
      if (disabled) {
        this.router.navigate(['/settings/subscription/unavailable']);
      }
    });

    if (this.deviceDetectionService.isIosApp()) {
      this.router.navigate(['/settings/subscription/denied']);
      return;
    }

    this.user = this.authService.getUserDetails();

    this.getSubscriptionData();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getSubscriptionData() {
    this.billingService.getSubscriptionDetails().subscribe((res) => {
      this.subscriptionData = res;

      if (!(this.subscriptionData.status === 'trialing' && this.subscriptionData?.plan?.name === 'legacy')) {
        this.router.navigate(['/settings/your-plan']);
      }

      this.loading = false;
    });
  }

  billingSaved() {
    this.loading = true;
    this.getSubscriptionData();
    this.stage = 2;
  }

  paymentSaved(newMethodDetails: { type: 'paypal' | 'card'; token: string }) {
    if (newMethodDetails) {
      this.stashedNewPaymentMethod = newMethodDetails;
    }

    this.loading = true;
    this.getSubscriptionData();
    this.stage = 3;
  }

  returnToStage(stage: number) {
    this.stage = stage;
  }

  finaliseSubscription() {
    this.subscribing = true;

    this.billingService
      .attemptSubscribe(this.subscriptionData.active_properties, this.stashedNewPaymentMethod)
      .subscribe(
        (res) => {
          this.subscribing = false;

          if (res.error) {
            if (res.error_reason === SubscriptionErrorReasons.SubscriptionCannotBeResubscribedTo) {
              this.router.navigate(['/settings/your-plan']);
              return;
            }

            if (res.error_reason === SubscriptionErrorReasons.ActiveListingCountHasChanged) {
              this.getSubscriptionData();
            }

            if (res.error_reason === SubscriptionErrorReasons.PaymentMethodCouldNotBeCreated) {
              this.billingSaved();
            }

            if (res.error_reason === SubscriptionErrorReasons.SubscriptionCannotBeCreated) {
              this.billingSaved();

              this.intercomService.investigateSubscriptionIssue();
            }

            this.toast.open(`${res.message ? res.message : res.error_reason}`, 'Dismiss', NotificationType.Error);

            return;
          }

          this.subscribing = false;

          // Send the subscription event to Segment
          this.segmentIoService.track(SegmentEvent.AccountSubscribed, {
            label: 'Converted',
          });

          this.router.navigate(['/settings/your-plan']);
        },
        (err) => {
          console.error('Error subscribing', err);
        }
      );
  }
}
